import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '../../../func';
import endpoints from '../../../common/endpoints.ts';
import api from '../../../common/api/index.ts';
import { useParams } from 'react-router-dom';
import NewFileUploader from '../../../utilities/newFileUploader.ts';

type RouteParams = {
  id: string;
};
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const FileUpload = () => {
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { id } = useParams<RouteParams>();

  const tradingPartnerIdNumber = Number(id);

  const tradingPartnerQueryKey = [
    endpoints.tradingPartner.read.all.attachments.by.tradingPartnerId(),
  ];

  const cancelOngoingRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const createAbortSignal = () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  };

  const handleToast = errOrMessage => {
    if (typeof errOrMessage === 'string') {
      toast(errOrMessage);
      return;
    }

    if (errOrMessage?.response?.data) {
      toast(errOrMessage.response.data);
    } else {
      console.log(errOrMessage);
    }
  };

  const { data: uploadedFiles, isLoading } = useQuery({
    queryKey: tradingPartnerQueryKey,
    queryFn: () =>
      api.tradingPartner.read.all.attachments.by.tradingPartnerId(
        tradingPartnerIdNumber
      ),
  });

  const uploadMutation = useMutation({
    mutationFn: async (files: []) => {
      const uploader = new NewFileUploader(
        files,
        endpoints.tradingPartner.upload.attachment(),
        { tradingPartnerId: tradingPartnerIdNumber }
      );

      return await uploader.upload();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradingPartnerQueryKey });
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const downloadFileMutation = useMutation({
    mutationFn: async file => {
      cancelOngoingRequest();
      const signal = createAbortSignal();

      const downloadBuffer =
        await api.tradingPartner.download.one.attachment.by.id(file.id, signal);

      const contentType = file.contentType;
      const fileName = file.filename;

      const blob = new Blob([downloadBuffer], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async file => {
      cancelOngoingRequest();
      const signal = createAbortSignal();

      await api.tradingPartner.delete.one.attachment.by.id(file.id, signal);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradingPartnerQueryKey });
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files: File[] = Array.from(input.files || []);

    if (files.length === 0) return;

    if (uploadedFiles && uploadedFiles?.length + files?.length > 10) {
      toast({
        payload: {
          msg: 'The addition of these files exceeds your limit of 10 files',
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }

    const oversizeFileBatch = files.reduce(
      (total, file) => total + file.size,
      0
    );

    if (oversizeFileBatch > MAX_FILE_SIZE) {
      toast({
        payload: {
          msg: `"Total file(s) size exceeds limit (max 50Mb).`,
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }

    const filenames = files.map(file => file.name);
    const existingFile = uploadedFiles?.find(uploadedFile =>
      filenames.includes(uploadedFile?.filename)
    );
    if (existingFile) {
      toast({
        payload: {
          msg: `${existingFile?.filename} has already been uploaded`,
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }

    uploadMutation.mutate(files);
    input.value = '';
  };

  const downloadFile = file => {
    downloadFileMutation.mutate(file);
  };

  const deleteFile = file => {
    deleteMutation.mutate(file);
  };

  const openUploadBrowse = () => {
    if ((uploadedFiles?.length || 0) > 9) {
      return toast({
        payload: {
          msg: 'You have reached your limit of 10 files',
          class: 'red white-text',
        },
      });
    }

    fileInputRef.current?.click();
  };

  const isBusy =
    isLoading ||
    uploadMutation.isPending ||
    deleteMutation.isPending ||
    downloadFileMutation.isPending;

  return (
    <>
      {isBusy ? (
        <div className="progress">
          <div className="indeterminate" />
        </div>
      ) : (
        <>
          <div className="row">
            <h5>Uploaded Files</h5>
            <div className="divider" />
          </div>
          <div className="row">
            {uploadedFiles?.length ? (
              <table className="highlight">
                <thead>
                  <tr>
                    <th style={{ padding: '2px' }}>Filename</th>
                    <th style={{ padding: '2px' }}>Uploaded By</th>
                    <th style={{ padding: '2px' }}>Date Uploaded</th>
                    <th style={{ padding: '2px' }}></th>
                    <th style={{ padding: '2px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedFiles?.map(file => (
                    <tr
                      key={file.id}
                      className="hoverable"
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{file.filename}</td>
                      <td>
                        {file.uploadedBy.firstName} {file.uploadedBy.lastName}
                      </td>
                      <td>{file.dateUploaded}</td>
                      <td onClick={() => downloadFile(file)}>
                        <i
                          className="material-icons hoverable"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10px',
                            height: '30px',
                            width: '30px',
                            cursor: 'pointer',
                          }}
                        >
                          cloud_download
                        </i>
                      </td>
                      <td onClick={() => deleteFile(file)}>
                        <i
                          className="material-icons hoverable"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '10px',
                            height: '30px',
                            width: '30px',
                            cursor: 'pointer',
                          }}
                        >
                          delete
                        </i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ width: '100%', textAlign: 'center' }}>
                There are currently no files uploaded for this Trading Partner
              </div>
            )}
            <div
              className="row"
              style={{ margin: '20px', paddingBottom: '20px' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <button
                className="btn-small blue white-text waves-effect waves-light col s4 offset-s4"
                onClick={openUploadBrowse}
              >
                Upload
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FileUpload;
